import styled from '@emotion/styled';
import React from 'react';
import theme from '../../theme';
import Button from '../Button';
import { Box, Flex } from '../Rebass';
import Text from '../Text';

const updatePageInUrl = (pathname: string, search: string, page: number) => {
  const params = new URLSearchParams(search);
  if (page === 1) {
    params.delete('page');
  } else {
    params.set('page', String(page));
  }
  return params.toString() ? `${pathname}?${params.toString()}` : pathname;
};

interface PaginationProps {
  currentPage: number;
  numberOfPages: number;
  goToPage: (pageNumber: number) => void;
  padding?: number;
  LinkComponent?: React.ElementType;
  to?: string;
  search?: string;
}

const NavButton = styled(Flex)`
  justify-content: center;
  align-items: center;
  background-color: ${theme.colors.baseGrayVeryLight};
  width: 30px;
  height: 30px;
  border-radius: 4px;
  transform: rotate(135deg);
  cursor: pointer;

  & .icon {
    font-size: 10px;
    transform: rotate(-135deg);
  }
`;

const PageNumber = styled(Flex)(
  ({ isCurrentPage }: { isCurrentPage?: boolean }) => `
  border-radius: 50%;
  background-color: ${
    isCurrentPage ? theme.colors.brandBrown : theme.colors.transparent
  };
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s ease, opacity ${theme.speed.default} ease;
  will-change: background-color, opacity;

  & span {
    transition: color 0.3s ease;
  }

  opacity: ${isCurrentPage ? theme.opacity.full : theme.opacity.medium};

  &:hover {
    opacity: ${theme.opacity.full};
    background-color: ${theme.colors.baseGrayVeryLight};
  }
`,
);

const Pagination = ({
  currentPage,
  numberOfPages,
  goToPage,
  padding = 3,
  LinkComponent,
  to,
  search,
}: PaginationProps) => {
  if (numberOfPages <= 1) {
    return null;
  }
  const pageNumbers = Array(numberOfPages)
    .fill(0)
    .map((_, idx) => idx + 1);

  let sliced;
  if (currentPage <= padding + 1) {
    sliced = pageNumbers.slice(0, padding * 2 + 1);
  } else if (currentPage > numberOfPages - (padding + 1)) {
    sliced = pageNumbers.slice(numberOfPages - (padding * 2 + 1));
  } else {
    sliced = pageNumbers.slice(
      currentPage - (padding + 1),
      currentPage + padding,
    );
  }

  return (
    <>
      <Box
        pl={[1]}
        sx={{ display: ['none', 'none', 'flex', 'flex'], alignItems: 'center' }}
      >
        {!sliced.includes(1) && (
          <>
            <Button
              as={LinkComponent}
              variant="icon"
              size="small"
              colorScheme="transparent"
              iconColor="textDark"
              icon="chevron-double-left"
              onClick={!LinkComponent || !to ? () => goToPage(1) : undefined}
              to={
                !!LinkComponent && !!to
                  ? updatePageInUrl(to, search, 1)
                  : undefined
              }
            />
            <Button
              as={LinkComponent}
              variant="icon"
              size="small"
              colorScheme="transparent"
              iconColor="textDark"
              icon="chevron-left"
              data-testid="prev-button"
              onClick={
                !LinkComponent || !to
                  ? () => goToPage(currentPage - 1)
                  : undefined
              }
              to={
                !!LinkComponent && !!to
                  ? updatePageInUrl(to, search, currentPage - 1)
                  : undefined
              }
            />
          </>
        )}
        {sliced.map((pageNumber) => {
          const isCurrentPage = pageNumber === currentPage;
          return (
            <PageNumber
              key={pageNumber}
              as={LinkComponent}
              isCurrentPage={isCurrentPage}
              onClick={
                !LinkComponent || !to ? () => goToPage(pageNumber) : undefined
              }
              to={
                !!LinkComponent && !!to
                  ? updatePageInUrl(to, search, pageNumber)
                  : undefined
              }
              mr={[1]}
            >
              <Text
                size="small"
                color={isCurrentPage ? 'textWhite' : 'textDark'}
                fontWeight="bold"
              >
                {pageNumber}
              </Text>
            </PageNumber>
          );
        })}
        {!sliced.includes(numberOfPages) && (
          <>
            <Button
              as={LinkComponent}
              variant="icon"
              size="small"
              colorScheme="transparent"
              iconColor="textDark"
              icon="chevron-right"
              data-testid="next-button"
              onClick={
                !LinkComponent || !to
                  ? () => goToPage(currentPage + 1)
                  : undefined
              }
              to={
                !!LinkComponent && !!to
                  ? updatePageInUrl(to, search, currentPage + 1)
                  : undefined
              }
            />
            <Button
              as={LinkComponent}
              variant="icon"
              size="small"
              colorScheme="transparent"
              iconColor="textDark"
              icon="chevron-double-right"
              onClick={
                !LinkComponent || !to
                  ? () => goToPage(numberOfPages)
                  : undefined
              }
              to={
                !!LinkComponent && !!to
                  ? updatePageInUrl(to, search, numberOfPages)
                  : undefined
              }
            />
          </>
        )}
      </Box>
      <Box
        sx={{
          display: ['flex', 'flex', 'none', 'none'],
          justifyContent: 'space-between',
        }}
      >
        <Flex
          as={LinkComponent}
          sx={{
            opacity: currentPage > 1 ? theme.opacity.full : theme.opacity.feint,
            pointerEvents: currentPage > 1 ? 'auto' : 'none',
            cursor: 'pointer',
          }}
          alignItems="center"
          onClick={
            !!LinkComponent && !!to && currentPage > 1
              ? () => goToPage(currentPage - 1)
              : undefined
          }
          to={
            !!LinkComponent && !!to && currentPage > 1
              ? updatePageInUrl(to, search, currentPage - 1)
              : undefined
          }
        >
          <Box mr={[1]}>
            <Button
              as={LinkComponent}
              variant="icon"
              icon="chevron-left"
              colorScheme="bgLight"
            />
          </Box>
          <Text size="body" color="textDark">
            Previous
          </Text>
        </Flex>
        <Flex
          as={LinkComponent}
          sx={{
            opacity:
              currentPage < numberOfPages
                ? theme.opacity.full
                : theme.opacity.feint,
            pointerEvents: currentPage < numberOfPages ? 'auto' : 'none',
            cursor: 'pointer',
          }}
          alignItems="center"
          onClick={
            !!LinkComponent && !!to && currentPage < numberOfPages
              ? () => goToPage(currentPage + 1)
              : undefined
          }
          to={
            !!LinkComponent && !!to && currentPage < numberOfPages
              ? updatePageInUrl(to, search, currentPage + 1)
              : undefined
          }
        >
          <Text size="body" color="textDark">
            Next
          </Text>
          <Box ml={[1]}>
            <Button
              as={LinkComponent}
              variant="icon"
              icon="chevron-right"
              colorScheme="bgLight"
            />
          </Box>
        </Flex>
      </Box>
    </>
  );
};

export default Pagination;
